import logoPadrao from '@/assets/logo.png';
import { BASE_URL } from '@/axiosConfig';
import UserWidget from './UserWidget';
import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { 
  House, 
  ShoppingCart, 
  Users, 
  Banknote, 
  AreaChart, 
  PiggyBank,
  ChevronDown,
  ArrowUp,
  ArrowDown,
  FileText,
  Plane,
  TrendingUp,
  Trophy,
  Zap,
  Truck,
  Briefcase,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

// Componente NavItem (Mantido igual)
const NavItem = ({ to, icon: Icon, children, isCollapsed }) => (
  <NavLink
    to={to}
    end 
    className={({ isActive }) =>
      `flex items-center px-4 py-3 text-sm font-medium rounded-md transition-all duration-200 group ${
        isActive
          ? 'bg-blue-600 text-white'
          : 'text-gray-300 hover:bg-gray-700 hover:text-white'
      } ${isCollapsed ? 'justify-center' : ''}`
    }
    title={isCollapsed && typeof children === 'string' ? children : ''}
  >
    <Icon className={`w-5 h-5 ${!isCollapsed ? 'mr-3' : ''}`} />
    {!isCollapsed && <span className="truncate">{children}</span>}
    {isCollapsed && (
      <div className="absolute left-16 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50 whitespace-nowrap hidden group-hover:block shadow-lg border border-gray-700">
        {children}
      </div>
    )}
  </NavLink>
);

const SubNavItem = ({ to, icon: Icon, children, isCollapsed }) => (
  <NavLink
    to={to}
    end
    className={({ isActive }) =>
      `flex items-center px-4 py-2 text-sm font-medium rounded-md transition-colors ${
        isActive
          ? 'bg-gray-600 text-white'
          : 'text-gray-400 hover:bg-gray-700 hover:text-white'
      } ${isCollapsed ? 'justify-center' : ''}`
    }
    title={isCollapsed && typeof children === 'string' ? children : ''}
  >
    <Icon className={`w-4 h-4 ${!isCollapsed ? 'mr-3' : ''}`} />
    {!isCollapsed && <span className="truncate">{children}</span>}
  </NavLink>
);

export default function Sidebar() {
  // Estado para armazenar o usuário e forçar renderização quando mudar
  const [user, setUser] = useState(JSON.parse(localStorage.getItem('user') || '{}'));
  const isSuperUser = user.is_superuser;

  const [isCollapsed, setIsCollapsed] = useState(false);
  const location = useLocation(); 

  const [isFinanceiroOpen, setIsFinanceiroOpen] = useState(false);
  const [isRelatoriosOpen, setIsRelatoriosOpen] = useState(false);
  const [isPessoasOpen, setIsPessoasOpen] = useState(false);

  // --- LÓGICA DE ATUALIZAÇÃO DA LOGO E USUÁRIO ---
  useEffect(() => {
    // Função para atualizar estado lendo do localStorage
    const handleUserUpdate = () => {
        const updatedUser = JSON.parse(localStorage.getItem('user') || '{}');
        setUser(updatedUser);
    };

    // Escuta evento customizado (disparado pelo Perfil ou Login)
    window.addEventListener('userUpdated', handleUserUpdate);
    // Escuta evento padrão de storage (para mudanças em outras abas)
    window.addEventListener('storage', handleUserUpdate);

    return () => {
        window.removeEventListener('userUpdated', handleUserUpdate);
        window.removeEventListener('storage', handleUserUpdate);
    };
  }, []);

  let logoSrc = logoPadrao;

    if (user && user.logo_url) {
        if (user.logo_url.startsWith('http') || user.logo_url.startsWith('data:')) {
            logoSrc = user.logo_url;
        } else {
            // Garante que não duplique barras se o BASE_URL já tiver
            logoSrc = `${BASE_URL}${user.logo_url}`;
        }
    }
  const sidebarStyle = user.cor_primaria ? { backgroundColor: user.cor_primaria } : { backgroundColor: '#1f2937' };

  useEffect(() => {
    if (location.pathname.startsWith('/pessoas')) setIsPessoasOpen(true);
    if (location.pathname.startsWith('/financeiro')) setIsFinanceiroOpen(true);
    if (location.pathname.startsWith('/relatorios')) setIsRelatoriosOpen(true);
  }, [location.pathname]);

  const handleToggleSubmenu = (currentState, setState) => {
    if (isCollapsed) {
        setIsCollapsed(false);
        setState(true);
    } else {
        setState(!currentState);
    }
  };

  return (
    <aside 
      className={`
        flex flex-col h-full flex-shrink-0 
        transition-all duration-300 ease-in-out
        px-3 py-6 text-white overflow-y-auto overflow-x-hidden
        ${isCollapsed ? 'w-20' : 'w-64'}
      `}
      style={sidebarStyle}
    >
      
      {/* Cabeçalho: Logo + Botão de Toggle */}
      <div className={`mb-8 flex items-center ${isCollapsed ? 'justify-center' : 'justify-between px-1'}`}>
        
        {!isCollapsed && (
            <img 
            src={logoSrc} 
            alt="Logo" 
            className="h-14 w-auto object-contain max-w-[160px] transition-opacity" 
            // Adiciona tratamento de erro para voltar ao padrão se o link quebrar
            onError={(e) => { e.target.onerror = null; e.target.src = logoPadrao; }}
            />
        )}
        
        <button 
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="p-1.5 rounded hover:bg-white/10 text-gray-300 hover:text-white transition-colors ml-2"
        >
            {isCollapsed ? <ChevronRight className="w-6 h-6" /> : <ChevronLeft className="w-6 h-6" />}
        </button>
      </div>

      <div className="mb-4">
        {!isCollapsed ? (
            <UserWidget />
        ) : (
            <div className="flex justify-center" title={user.name || 'Usuário'}>
                <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center font-bold text-white shadow-md cursor-default">
                    {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                </div>
            </div>
        )}
      </div>

      <nav className="flex flex-col space-y-2 flex-1">
        <NavItem to="/" icon={House} isCollapsed={isCollapsed}>Início</NavItem>
      
        {/* === MENU PESSOAS === */}
        <div>
          <button
            onClick={() => handleToggleSubmenu(isPessoasOpen, setIsPessoasOpen)}
            className={`flex items-center w-full px-4 py-3 text-sm font-medium text-gray-300 rounded-md hover:bg-gray-700 hover:text-white transition-colors ${isCollapsed ? 'justify-center' : 'justify-between'}`}
            title={isCollapsed ? "Cadastros" : ""}
          >
            <div className="flex items-center">
              <Users className={`w-5 h-5 ${!isCollapsed ? 'mr-3' : ''}`} />
              {!isCollapsed && "Cadastros"}
            </div>
            {!isCollapsed && (
                <ChevronDown className={`w-5 h-5 transition-transform ${isPessoasOpen ? 'rotate-180' : ''}`} />
            )}
          </button>
          
          {isPessoasOpen && !isCollapsed && (
            <div className="mt-1 pl-6 space-y-1 border-l border-gray-600 ml-4">
              <SubNavItem to="/clientes" icon={Users} isCollapsed={isCollapsed}>Clientes</SubNavItem>
              <SubNavItem to="/pessoas/colaboradores" icon={Briefcase} isCollapsed={isCollapsed}>Colaboradores</SubNavItem>
              <SubNavItem to="/pessoas/fornecedores" icon={Truck} isCollapsed={isCollapsed}>Fornecedores</SubNavItem>
              <SubNavItem to="/pessoas/contas" icon={Banknote} isCollapsed={isCollapsed}>Contas Bancárias</SubNavItem>
            </div>
          )}
        </div>     

        <NavItem to="/vendas" icon={ShoppingCart} isCollapsed={isCollapsed}>Vendas</NavItem>
        <NavItem to="/voos" icon={Plane} isCollapsed={isCollapsed}>Gerenciador Voos</NavItem>

        {/* === MENU FINANCEIRO === */}
        <div>
          <button
            onClick={() => handleToggleSubmenu(isFinanceiroOpen, setIsFinanceiroOpen)}
            className={`flex items-center w-full px-4 py-3 text-sm font-medium text-gray-300 rounded-md hover:bg-gray-700 hover:text-white transition-colors ${isCollapsed ? 'justify-center' : 'justify-between'}`}
            title={isCollapsed ? "Financeiro" : ""}
          >
            <div className="flex items-center">
              <PiggyBank className={`w-5 h-5 ${!isCollapsed ? 'mr-3' : ''}`} />
              {!isCollapsed && "Financeiro"}
            </div>
            {!isCollapsed && (
                <ChevronDown className={`w-5 h-5 transition-transform ${isFinanceiroOpen ? 'rotate-180' : ''}`} />
            )}
          </button>
          
          {isFinanceiroOpen && !isCollapsed && (
            <div className="mt-1 pl-6 space-y-1 border-l border-gray-600 ml-4">
              <SubNavItem to="/financeiro/dre" icon={Banknote} isCollapsed={isCollapsed}>DRE (Faturamento)</SubNavItem>
              <SubNavItem to="/financeiro/fluxo-caixa" icon={FileText} isCollapsed={isCollapsed}>Fluxo de Caixa</SubNavItem>
              <SubNavItem to="/financeiro/contas-a-pagar" icon={ArrowUp} isCollapsed={isCollapsed}>Contas a Pagar</SubNavItem>
              <SubNavItem to="/financeiro/contas-a-receber" icon={ArrowDown} isCollapsed={isCollapsed}>Contas a Receber</SubNavItem>
            </div>
          )}
        </div>
        
        {/* === MENU RELATÓRIOS === */}
        <div>
          <button
            onClick={() => handleToggleSubmenu(isRelatoriosOpen, setIsRelatoriosOpen)}
            className={`flex items-center w-full px-4 py-3 text-sm font-medium text-gray-300 rounded-md hover:bg-gray-700 hover:text-white transition-colors ${isCollapsed ? 'justify-center' : 'justify-between'}`}
            title={isCollapsed ? "Relatórios" : ""}
          >
            <div className="flex items-center">
              <AreaChart className={`w-5 h-5 ${!isCollapsed ? 'mr-3' : ''}`} />
              {!isCollapsed && "Relatórios (BI)"}
            </div>
            {!isCollapsed && (
                <ChevronDown className={`w-5 h-5 transition-transform ${isRelatoriosOpen ? 'rotate-180' : ''}`} />
            )}
          </button>
          
          {isRelatoriosOpen && !isCollapsed && (
            <div className="mt-1 pl-6 space-y-1 border-l border-gray-600 ml-4">
              <SubNavItem to="/relatorios/rankings" icon={Trophy} isCollapsed={isCollapsed}>Rankings & Tops</SubNavItem>
              <SubNavItem to="/relatorios/evolucao" icon={TrendingUp} isCollapsed={isCollapsed}>Evolução Mensal</SubNavItem>
            </div>
          )}
        </div>
      </nav>

      {isSuperUser && (
            <div className={`pt-4 mt-4 border-t border-gray-700 ${isCollapsed ? 'flex justify-center' : ''}`}>
                {!isCollapsed && (
                    <p className="px-4 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Administração</p>
                )}
                <NavItem to="/admin-god-mode" icon={Zap} isCollapsed={isCollapsed}>God Mode</NavItem>
            </div>
        )}
    </aside>
  );
}